import { useMediaPermission } from "src/contexts/MediaPermissionContext";
import { useState, useRef, useEffect, useCallback } from "react";

interface UseCameraStreamProps {
    initialActive?: boolean;
}

interface UseCameraStreamReturn {
    videoRef: React.RefObject<HTMLVideoElement | null>;
    cameraActive: boolean;
    hasPermission: boolean;
    toggleCamera: () => void;
}

const useCameraStream = ({
    initialActive = true,
}: UseCameraStreamProps = {}): UseCameraStreamReturn => {
    const { permissions, stream } = useMediaPermission();
    const [cameraActive, setCameraActive] = useState(initialActive);
    const videoRef = useRef<HTMLVideoElement>(null);
    const videoStreamRef = useRef<MediaStream | null>(null);
    const activeRef = useRef(initialActive); // Ref to track current active state

    // Toggle camera state with a callback to ensure latest state
    const toggleCamera = useCallback(() => {
        setCameraActive((prevActive) => {
            const newActive = !prevActive;
            console.log(`Camera toggled: ${prevActive} -> ${newActive}`);
            activeRef.current = newActive; // Update ref for use in effects
            return newActive;
        });
    }, []);

    // Apply camera state changes
    const applyCameraState = useCallback(() => {
        console.log(`Applying camera state: active=${activeRef.current}`);

        // Don't proceed if video element isn't ready
        if (!videoRef.current) {
            console.log("Video ref not ready");
            return;
        }

        // Don't proceed if no stream is available
        if (!stream) {
            console.log("No media stream available");
            return;
        }

        // Get video tracks from the stream
        const videoTracks = stream.getVideoTracks();
        console.log(`Found ${videoTracks.length} video tracks`);

        if (activeRef.current && videoTracks.length > 0 && permissions.video) {
            console.log("Enabling camera");
            // Create a new MediaStream with only the video tracks
            const videoOnlyStream = new MediaStream(videoTracks);
            videoStreamRef.current = videoOnlyStream;

            // Enable all video tracks
            videoTracks.forEach((track) => {
                track.enabled = true;
                console.log(`Enabled track: ${track.id}`);
            });

            // Set the stream to the video element
            videoRef.current.srcObject = videoOnlyStream;
            videoRef.current.style.transform = "scaleX(-1)";

            // Play the video (handle any errors)
            videoRef.current.play().catch((err) => {
                console.error("Error playing video:", err);
            });
        } else {
            console.log("Disabling camera");
            // If camera is not active or we don't have permission,
            // disable video tracks but don't stop them
            videoTracks.forEach((track) => {
                track.enabled = false;
                console.log(`Disabled track: ${track.id}`);
            });

            // Clear the video element
            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }

            // Clear the local stream reference
            videoStreamRef.current = null;
        }
    }, [stream, permissions.video]);

    // Watch for camera active state changes
    useEffect(() => {
        console.log(`Camera active state changed: ${cameraActive}`);
        applyCameraState();
    }, [cameraActive, applyCameraState]);

    // Also watch for stream changes
    useEffect(() => {
        console.log("Stream or permissions changed");
        applyCameraState();
    }, [stream, permissions.video, applyCameraState]);

    // Clean up when component unmounts
    useEffect(() => {
        return () => {
            console.log("Camera hook unmounting, cleaning up");
            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }
            videoStreamRef.current = null;
        };
    }, []);

    return {
        videoRef,
        cameraActive,
        hasPermission: !!permissions.video,
        toggleCamera,
    };
};

export default useCameraStream;
